/*
 * The pure half of the setup hub (`useSetup`): what the API answers (lists, resolutions, builds) turned into
 * the facts the checklist is computed from. No React and no API client, so it can be tested on its own.
 */
import type { Environment, Platform, Project } from '@hangar/shared';
import type { EnvResolution, Profile, ProviderAccount } from '@/api/schemas';
import type { SetupFacts } from './steps';

/** One environment's effective env for the selected app (`POST /apps/{id}/env/resolve`). */
export interface EnvTile {
  environment: Environment;
  platform: Platform;
  resolution: EnvResolution | undefined;
  loading: boolean;
  error: unknown;
}

/** The team's keys in a resolution: Hangar's own system entries (HANGAR_APP, HANGAR_ENVIRONMENT…) do not count. */
export const teamKeys = (r: EnvResolution | undefined): number | undefined => (r ? r.entries.filter((e) => e.source !== 'system').length : undefined);

/** The project's live environments, in its order. */
export const liveEnvironments = (project: Project | undefined): Environment[] => [...(project?.environments ?? [])].filter((e) => !e.archivedAt).sort((a, b) => a.sortOrder - b.sortOrder);

/** The platform an environment builds for: the first profile bound to it, else the app's first profile, else Android. */
export function platformFor(env: Environment, profiles: Profile[]): Platform {
  return profiles.find((p) => p.environments.some((b) => b.environmentId === env.id))?.platform ?? profiles[0]?.platform ?? 'android';
}

/** The bundle id a profile of the app checks (`verification.bundleId`), if any. */
export function bundleIdOf(profiles: Profile[] | undefined): string | undefined {
  for (const p of profiles ?? []) {
    const id = (p.verification as Record<string, unknown> | undefined)?.bundleId;
    if (typeof id === 'string' && id) return id;
  }
  return undefined;
}

/** Accounts builds can run on: not disabled, and EAS (Bitrise only once the API builds on it). */
export const usableAccountsOf = (accounts: ProviderAccount[], bitrise: boolean): ProviderAccount[] => accounts.filter((a) => a.status !== 'disabled' && (a.kind === 'eas' || bitrise));

/**
 * A profile Run build can start: Hangar's runner (Expo) or Bitrise's API once this Hangar builds on Bitrise. The CLI
 * (`ci_client`) does not count: CLI and CI builds return with docs/mvp-plan.md P17.
 */
export const isRunnable = (p: Profile, bitrise: boolean): boolean => p.executionModes.includes('hangar_runner') || (bitrise && p.executionModes.includes('provider_api'));

/** The profile First build starts: the first runnable one, else the first. */
export const runProfile = (profiles: Profile[], bitrise: boolean): Profile | undefined => profiles.find((p) => isRunnable(p, bitrise)) ?? profiles[0];

/** A Malible platform operator: sets up provider accounts and sees local-stack notes (one rule, in the integrations model). */
export { isPlatformOperator } from '@/features/integrations/providerAccountModel';

/** Env facts are known once every environment answered (an error counts as answered, with no keys). */
export const isEnvSettled = (hasApp: boolean, canReadEnv: boolean, tiles: Array<Pick<EnvTile, 'resolution' | 'error'>>): boolean => hasApp && canReadEnv && tiles.every((t) => t.resolution || t.error);

/** The builds of the app: the larger of its last build number and what the list shows; unknown until both are read. */
export const buildCount = (lastBuildNumber: number | undefined, listed: number | undefined): number | undefined => (lastBuildNumber === undefined || listed === undefined ? undefined : Math.max(lastBuildNumber, listed));

/** What the page has read so far, as plain numbers and lists. */
export interface FactInputs {
  usableAccounts: number;
  projects: number;
  apps: number;
  profiles: number;
  runnableProfiles: number;
  envTiles: Array<Pick<EnvTile, 'environment' | 'resolution'>>;
  envSettled: boolean;
  builds: number | undefined;
  buildsSucceeded: number;
}

export function buildFacts(i: FactInputs): SetupFacts {
  return {
    providerAccounts: i.usableAccounts,
    projects: i.projects,
    apps: i.apps,
    profiles: i.profiles,
    runnableProfiles: i.runnableProfiles,
    envKeys: i.envSettled ? Math.max(0, ...i.envTiles.map((t) => teamKeys(t.resolution) ?? 0)) : undefined,
    envMissing: i.envTiles.flatMap((t) => (t.resolution?.missing ?? []).map((m) => ({ key: m.key, environment: t.environment.slug }))),
    builds: i.builds,
    buildsSucceeded: i.buildsSucceeded,
  };
}

/** Which reads are still in flight, for the roles that can make them. */
export interface PendingInputs {
  canReadEnv: boolean;
  hasApp: boolean;
  envSettled: boolean;
  buildsLoading: boolean;
}

/** Still reading a fact the role can read: the hero waits instead of counting up as answers arrive. */
export const isPending = (p: PendingInputs): boolean => (p.canReadEnv && p.hasApp && !p.envSettled) || (p.hasApp && p.buildsLoading);

/** The entry with this slug, else the first one (the URL names the project and app; absent = the first). */
export const pickBySlug = <T extends { slug: string }>(list: T[] | undefined, slug: string | undefined): T | undefined => list?.find((x) => x.slug === slug) ?? list?.[0];

/** A result per id, keyed by id: `pick` reads the value out of the i-th query result. */
export function byId<T, R>(items: Array<{ id: string }> | undefined, results: Array<{ data?: T } | undefined>, pick: (data: T | undefined) => R): Record<string, R> {
  return Object.fromEntries((items ?? []).map((item, i) => [item.id, pick(results[i]?.data)]));
}
